/**
 * Beaute Plus — Alt-text based variant image grouping
 *
 * Convention: image alt text starts with one of the color names.
 *   "Hot Pink - product view"  → only shown when Hot Pink selected
 *   "Pink - lifestyle"          → only shown when Pink selected
 *   "tech detail"               → always shown (generic, no color prefix)
 *
 * "Hot Pink" precedence: sorted by length DESC so "Hot Pink" matches BEFORE
 * "Pink", preventing false-positive (alt "Hot Pink - 1" won't be matched as "Pink").
 *
 * Triggers:
 *   - DOMContentLoaded (initial state)
 *   - variant change events (variant-radios input change, variant-selects select change)
 *   - shopify:section:load (theme editor)
 */
(function () {
  const COLORS = ['Hot Pink', 'Pink', 'Yellow', 'Blue', 'Navy'];
  // Sorted longest-first to handle "Hot Pink" before "Pink"
  const COLORS_SORTED = [...COLORS].sort((a, b) => b.length - a.length);

  function findImageColor(altText) {
    if (!altText) return null;
    for (const color of COLORS_SORTED) {
      if (altText.startsWith(color)) return color;
    }
    return null;
  }

  function shouldShow(altText, currentColor) {
    const imageColor = findImageColor(altText);
    if (imageColor === null) return true; // generic image, always shown
    return imageColor === currentColor;
  }

  function getCurrentColor() {
    // Method 1: variant-radios — currently checked radio with color value
    const radios = document.querySelectorAll(
      'variant-radios input[type="radio"]:checked, fieldset input[type="radio"]:checked'
    );
    for (const radio of radios) {
      if (COLORS.includes(radio.value)) return radio.value;
    }

    // Method 2: variant-selects
    const selects = document.querySelectorAll('variant-selects select, fieldset select');
    for (const select of selects) {
      if (COLORS.includes(select.value)) return select.value;
    }

    // Method 3: Fallback — scan visible swatch-input labels with selected state
    const selectedLabels = document.querySelectorAll(
      '.product-form__input--swatch input:checked + label, .product-form__input--pill input:checked + label'
    );
    for (const label of selectedLabels) {
      const text = (label.textContent || '').trim();
      if (COLORS.includes(text)) return text;
    }

    return null;
  }

  function applyFilter() {
    const currentColor = getCurrentColor();
    if (!currentColor) return;

    // Filter thumbnails
    const thumbs = document.querySelectorAll('.thumbnail-list__item');
    let firstVisibleThumb = null;
    thumbs.forEach((item) => {
      const img = item.querySelector('img');
      const alt = img ? img.getAttribute('alt') || '' : '';
      const show = shouldShow(alt, currentColor);
      item.style.display = show ? '' : 'none';
      if (show && !firstVisibleThumb) firstVisibleThumb = item;
    });

    // Filter main gallery slides
    const mainItems = document.querySelectorAll('.product__media-item');
    let firstVisibleMain = null;
    mainItems.forEach((item) => {
      const img = item.querySelector('img');
      const alt = img ? img.getAttribute('alt') || '' : '';
      const show = shouldShow(alt, currentColor);
      // Use class instead of inline style for main items to coexist with Dawn's
      // .product__media-item:not(.is-active) { display: none } rule.
      if (show) {
        item.classList.remove('bp-hidden-by-color');
      } else {
        item.classList.add('bp-hidden-by-color');
      }
      if (show && !firstVisibleMain) firstVisibleMain = item;
    });

    // If currently-active main item is hidden, activate the first visible one
    const activeMain = document.querySelector('.product__media-item.is-active');
    if (activeMain && activeMain.classList.contains('bp-hidden-by-color') && firstVisibleMain) {
      activeMain.classList.remove('is-active');
      firstVisibleMain.classList.add('is-active');
    }

    // Sync thumbnail active state to match
    const activeThumb = document.querySelector('.thumbnail-list__item.is-active');
    if (activeThumb && activeThumb.style.display === 'none' && firstVisibleThumb) {
      activeThumb.classList.remove('is-active');
      firstVisibleThumb.classList.add('is-active');
      // Update aria-current on thumbnail buttons
      document.querySelectorAll('.thumbnail-list__item .thumbnail[aria-current]').forEach((btn) => {
        btn.setAttribute('aria-current', 'false');
      });
      const newActiveBtn = firstVisibleThumb.querySelector('.thumbnail');
      if (newActiveBtn) newActiveBtn.setAttribute('aria-current', 'true');
    }
  }

  function init() {
    applyFilter();

    // Listen for variant changes — covers variant-radios, variant-selects, swatch-input, etc.
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!target || target.matches('input[type="radio"], select')) {
        // Defer to next frame so Dawn's variant logic runs first
        requestAnimationFrame(applyFilter);
      }
    });

    // Shopify product form variant change custom event (Dawn dispatches on variant pick)
    document.addEventListener('on:variant:change', () => requestAnimationFrame(applyFilter));
    // Some Dawn forks use this event
    document.addEventListener('variant:change', () => requestAnimationFrame(applyFilter));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
